import { useEffect, useState } from "react";
import { ClipboardList, Clock, Timer, ChevronRight, Mic } from "lucide-react";
import Card from "../foundational/Card";
import { horaHHMM } from "../../lib/registroTurno";
import {
  inicioTurnoTs, finTurnoTs, faseDelTurno, faltaTxt, proximaRondaTs,
  FASE_AHORA, TIPO_TURNO_LABEL,
} from "../../lib/turnoCockpit";
import { nombreServicio } from "../../constants/rotaciones";

// Cockpit del turno: una sola pieza de estado vivo que encabeza el hub de Mi
// Turno y aparece en la zona "Hoy" del home. Misma información, dos pesos:
//   - variant="home": héroe sólido (acceso prominente, toda la tarjeta navega).
//   - variant="hub":  header detallado dentro del módulo (informativo).
//
// El héroe del home se rellena con el ACENTO DEL TEMA (`bg-accent`), no con el
// color del módulo (2026-09-19, pantallazo del fundador con el tema Rosado:
// "era tb la caja del home"). Es una caja grande y sólida en una pantalla que
// NO es del módulo, así que pintarla del azul de MiPAE en turno dejaba un
// rectángulo frío pegado en un home rosado, el mismo defecto que ya se había
// corregido en los fondos de módulo ese día. Y es lo que el contrato de
// principios v2 pide desde siempre: el color del módulo vive en navegación y
// micro-acentos, "NO fondo grande".
//
// Adentro del módulo NO cambia nada, y por dos razones que conviene no
// confundir: esta rama es solo la del home (la del hub arranca en `esHome`), y
// además `bg-accent` lee `--accent-rgb`, que `RegistroTurno` no sobrescribe
// cuando reasigna el acento al color del módulo. La identidad de MiPAE en
// turno sigue intacta donde importa, que es dentro de MiPAE en turno.
//
// Props:
//   rotacion     — para servicio/hospital.
//   turnoHoy     — turno calculado por el caller (respeta overrides del home).
//   nPacientes   — pacientes en seguimiento del turno en curso.
//   ronda        — { inicio, num } | null (ronda 2 h viva).
//   variant      — "home" | "hub".
//   onAbrir      — abrir MiPAE en turno (home).
//   onIrRonda    — ir a la ronda (hub).
//   accionCama   — { cama, onRegistrar } | null (solo home): la vuelta al
//                  paciente en curso vive DENTRO del cockpit (retoque pre-Play
//                  2026-08-02: antes eran dos cards compitiendo por el mismo
//                  destino), con el micrófono a un toque.
export default function CockpitTurno({
  rotacion, turnoHoy = null, nPacientes = 0, ronda = null,
  variant = "hub", onAbrir, onIrRonda, accionCama = null, className = "",
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const esHome = variant === "home";
  const servicio = nombreServicio(rotacion) || null;
  const hospital = rotacion?.hospital || null;
  const enTurno = !!(turnoHoy && turnoHoy.tipo !== "libre" && turnoHoy.horaInicio);
  const fase = faseDelTurno(turnoHoy, new Date(now));

  // Etiqueta de estado: solo hablamos de "fase" si realmente hay turno hoy.
  // En día libre con pacientes arrastrados → "Seguimiento abierto", no "En turno".
  const estadoLabel = enTurno
    ? FASE_AHORA[fase]
    : (nPacientes > 0 ? "Seguimiento abierto" : "MiPAE en turno");

  // Cuenta regresiva según la fase: empieza (antes) / termina (durante).
  const iniTs = enTurno ? inicioTurnoTs(turnoHoy, new Date(now)) : null;
  const finTs = enTurno ? finTurnoTs(turnoHoy, new Date(now)) : null;
  const iniFalta = iniTs ? faltaTxt(iniTs - now) : null;
  const finFalta = finTs ? faltaTxt(finTs - now) : null;

  // Ronda viva. "Inminente" (≤15 min) tinta la fila con color clínico de
  // atención: el cockpit avisa con color, no con notificación intrusiva.
  const rondaActiva = !!ronda;
  const rondaTs = proximaRondaTs(ronda);
  const rondaTxt = rondaTs ? horaHHMM(new Date(rondaTs)) : null;
  const rondaFalta = rondaTs ? faltaTxt(rondaTs - now) : null;
  const rondaInminente = !!(rondaTs && rondaTs - now > 0 && rondaTs - now <= 15 * 60 * 1000);

  // Eyebrow: contexto (servicio + tipo de turno) o, si no hay turno, pacientes.
  const eyebrow = (() => {
    const partes = [];
    if (servicio) partes.push(servicio);
    if (enTurno) partes.push(TIPO_TURNO_LABEL[turnoHoy.tipo]);
    if (partes.length) return partes.join(" · ");
    return nPacientes > 0 ? "Pacientes en seguimiento" : "Tu turno";
  })();

  // Línea principal de agenda (la más relevante según el momento).
  const agenda = (() => {
    if (enTurno && fase === "antes" && iniFalta) return { icon: Clock, txt: `Empieza ${turnoHoy.horaInicio}`, sub: `en ${iniFalta}` };
    if (enTurno && fase === "durante" && finFalta) return { icon: Clock, txt: `Termina ${turnoHoy.horaFin}`, sub: `faltan ${finFalta}` };
    if (enTurno && fase === "cierre") return { icon: Clock, txt: "Turno terminado", sub: "ciérralo cuando puedas" };
    if (nPacientes > 0) return { icon: ClipboardList, txt: `${nPacientes} ${nPacientes === 1 ? "paciente" : "pacientes"} en seguimiento`, sub: null };
    return null;
  })();

  const AgendaIcon = agenda?.icon;

  // ── Variante HOME: héroe sólido, toda la tarjeta navega a MiPAE en turno ──────────
  if (esHome) {
    const cuerpo = (
      <>
        <div className="flex items-center gap-3">
          <ClipboardList size={22} strokeWidth={1.7} className="flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-micro uppercase tracking-wide opacity-80 font-sans">{estadoLabel}</p>
            <p className="mt-0.5 text-body font-semibold font-sans">MiPAE en turno</p>
            <p className="mt-0.5 text-small opacity-90 font-sans truncate">{eyebrow}</p>
          </div>
          <ChevronRight size={18} strokeWidth={2} className="flex-shrink-0 opacity-80" />
        </div>

        {(agenda || rondaActiva) && (
          <div className="mt-3 pt-3 border-t border-paper/20 flex flex-col gap-1.5">
            {agenda && (
              <div className="flex items-center gap-2 text-small font-sans">
                {AgendaIcon && <AgendaIcon size={14} strokeWidth={1.8} className="flex-shrink-0 opacity-90" />}
                <span>{agenda.txt}</span>
                {agenda.sub && <span className="opacity-75">· {agenda.sub}</span>}
              </div>
            )}
            {rondaActiva && (
              <div className="flex items-center gap-2 text-small font-sans">
                <Timer size={14} strokeWidth={1.8} className="flex-shrink-0 opacity-90" />
                <span>Próxima ronda ~{rondaTxt}</span>
                {rondaFalta && <span className="opacity-75">· en {rondaFalta}</span>}
              </div>
            )}
          </div>
        )}
      </>
    );

    if (!accionCama) {
      return (
        <button
          type="button"
          onClick={onAbrir}
          className={`w-full text-left rounded-tk-md bg-accent text-paper p-5 hover:opacity-95 active:scale-[0.99] transition-[opacity,transform] ${className}`}
        >
          {cuerpo}
        </button>
      );
    }

    // Con cama en curso: UNA card del momento. Arriba el turno navega al hub;
    // abajo la vuelta al paciente con el micrófono a un toque. Dos botones
    // hermanos (jamás un botón dentro de otro), mismo bloque visual.
    return (
      <div className={`w-full rounded-tk-md bg-accent text-paper overflow-hidden ${className}`}>
        <button
          type="button"
          onClick={onAbrir}
          className="w-full text-left p-5 pb-4 hover:opacity-95 active:scale-[0.995] transition-[opacity,transform]"
        >
          {cuerpo}
        </button>
        <button
          type="button"
          onClick={() => accionCama.onRegistrar?.(accionCama.cama)}
          className="w-full text-left px-5 py-3.5 border-t border-paper/25 flex items-center gap-3 hover:opacity-95 active:scale-[0.995] transition-[opacity,transform]"
        >
          <span className="flex-shrink-0 grid place-items-center w-10 h-10 rounded-full bg-paper text-module-prepara-turno">
            <Mic size={18} strokeWidth={1.9} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-body font-semibold font-sans">Tu paciente de la cama {accionCama.cama}</span>
            <span className="block text-small opacity-90 font-sans">Sigue donde quedaste, con el micrófono a un toque.</span>
          </span>
          <ChevronRight size={18} strokeWidth={2} className="flex-shrink-0 opacity-80" />
        </button>
      </div>
    );
  }

  // ── Variante HUB: header detallado dentro del módulo ────────────────────────
  return (
    <Card padding="md" className={className}>
      {/* Contexto: servicio · hospital */}
      {(servicio || hospital) && (
        <p className="text-small text-ink-subtle font-sans">
          {servicio ? <>Estás en <span className="font-semibold text-ink">{servicio}</span></> : "Tu turno"}
          {hospital ? <> · <span className="text-ink">{hospital}</span></> : null}
        </p>
      )}

      {/* Turno de hoy + chip de fase */}
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        {enTurno ? (
          <span className="inline-flex items-center gap-1.5 text-small text-ink-muted font-sans">
            <Clock size={14} strokeWidth={1.7} className="text-module-prepara-turno" />
            {TIPO_TURNO_LABEL[turnoHoy.tipo]} · {turnoHoy.horaInicio}{turnoHoy.horaFin ? `–${turnoHoy.horaFin}` : ""}
          </span>
        ) : (
          // Con pacientes arrastrados NO se puede decir "sin turno programado":
          // arriba el resumen del cierre dice "Tu turno terminó" y las dos
          // líneas se contradecían. Se reusa el vocabulario que el propio
          // cockpit ya tiene para este caso (`estadoLabel`, arriba).
          <span className="text-small text-ink-muted font-sans">
            {nPacientes > 0 ? "Seguimiento abierto" : "Sin turno programado hoy"}
          </span>
        )}
        {enTurno && (
          <span className="inline-flex items-center gap-1.5 text-10px font-semibold uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-full font-sans bg-accent-bg text-module-prepara-turno">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-module-prepara-turno opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-module-prepara-turno" />
            </span>
            {FASE_AHORA[fase]}
          </span>
        )}
      </div>

      {/* Agenda viva: cuenta regresiva + próxima ronda. La línea de agenda cruza
          con un fundido corto cuando cambia el minuto (key la re-monta): se
          percibe que el reloj está vivo sin mirarlo fijo. */}
      {(agenda || nPacientes > 0) && (
        <div className="mt-3 pt-3 border-t border-edge flex flex-col gap-2">
          {agenda && (
            <div key={`${agenda.txt}·${agenda.sub || ""}`} className="flex items-center gap-2 text-small font-sans animate-cruce">
              {AgendaIcon && <AgendaIcon size={14} strokeWidth={1.7} className="flex-shrink-0 text-module-prepara-turno" />}
              <span className="text-ink">{agenda.txt}</span>
              {agenda.sub && <span className="text-ink-muted">· {agenda.sub}</span>}
            </div>
          )}
          {nPacientes > 0 && onIrRonda && (
            <button
              type="button"
              onClick={onIrRonda}
              className={`flex items-center gap-2 text-small font-sans text-left ${rondaInminente ? "rounded-tk-sm bg-clinical-attention-bg border border-clinical-attention-border px-2 py-1.5 -mx-2" : ""}`}
            >
              <Timer size={14} strokeWidth={1.7} className={`flex-shrink-0 ${rondaInminente ? "text-clinical-attention animate-pulse" : "text-module-prepara-turno"}`} />
              {rondaActiva ? (
                <>
                  <span className={rondaInminente ? "text-clinical-attention font-semibold" : "text-ink"}>Próxima ronda ~{rondaTxt}</span>
                  <span className={rondaInminente ? "text-clinical-attention" : "text-ink-muted"}>· {rondaFalta ? `en ${rondaFalta}` : "ahora"}</span>
                </>
              ) : (
                <span className="text-module-prepara-turno font-medium">Inicia tu ronda</span>
              )}
              <ChevronRight size={14} strokeWidth={1.8} className="ml-auto flex-shrink-0 text-ink-faint" />
            </button>
          )}
        </div>
      )}
    </Card>
  );
}
